import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { mondayOf, weekLabel } from "./weekUtils";
import { ChevronLeft, ChevronRight, Droplets, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const inp = "h-10 px-2 border border-stone-300 text-sm w-full";

export default function ScheduleTab({ blocks, goSetup }) {
  const [week, setWeek] = useState(mondayOf());
  const [rows, setRows] = useState({});
  const [saved, setSaved] = useState(null);
  const [et, setEt] = useState({});

  const load = async (w) => {
    const [{ data: sch }, { data: etData }] = await Promise.all([
      api.get(`/irrigation/schedule?week=${w}`),
      api.get(`/irrigation/et?week=${w}`).catch(() => ({ data: {} })),
    ]);
    setSaved(sch.rows);
    setEt(etData || {});
    const map = {};
    sch.rows.forEach((r) => { map[r.block_id] = { gpv: r.gallons_per_vine ?? "", soil_moisture: r.soil_moisture || "", visual_assessment: r.visual_assessment || "" }; });
    setRows(map);
  };
  useEffect(() => { load(week); }, [week]); // eslint-disable-line

  const shift = (n) => {
    const d = new Date(week + "T12:00:00"); d.setDate(d.getDate() + n * 7);
    setWeek(mondayOf(d));
  };

  const ready = blocks.filter((b) => b.system_gph);
  const notReady = blocks.filter((b) => !b.system_gph);
  const get = (id) => rows[id] || { gpv: "", soil_moisture: "", visual_assessment: "" };
  const set = (id, upd) => setRows((s) => ({ ...s, [id]: { ...get(id), ...upd } }));
  const savedFor = (id) => (saved || []).find((r) => r.block_id === id);
  const vinesOf = (b) => (b.acres && b.vines_per_acre ? Math.round(b.acres * b.vines_per_acre) : null);

  const save = async (b) => {
    const r = get(b.id);
    if (!r.gpv || Number(r.gpv) <= 0) { toast.error("Enter gallons per vine first"); return; }
    try {
      await api.post("/irrigation/schedule", {
        week_start: week, block_id: b.id, gallons_per_vine: Number(r.gpv),
        soil_moisture: r.soil_moisture || null, visual_assessment: r.visual_assessment || null,
      });
      toast.success(`${b.block || b.name} scheduled`);
      await load(week);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (b) => {
    const s = savedFor(b.id);
    if (!s) return;
    await api.delete(`/irrigation/schedule/${s.id}`);
    await load(week);
  };

  const calc = (b) => {
    const gpv = Number(get(b.id).gpv);
    const vines = b.acres && b.vines_per_acre ? b.acres * b.vines_per_acre : null;
    if (!gpv || !vines || !b.system_gph) return { gallons: null, hours: null, sets: null };
    const gallons = Math.round(gpv * vines);
    const hours = Math.round((gallons / b.system_gph) * 10) / 10;
    const sets = b.hours_per_set ? Math.round((hours / b.hours_per_set) * 10) / 10 : null;
    return { gallons, hours, sets };
  };

  // gal/vine ≈ ET0(in) × Kc × vine area(sqft) × 0.623 gal/sqft-in × deficit%
  const suggest = (b) => {
    const e = et[b.ranch_number];
    if (!e || !b.vines_per_acre) return null;
    const kc = b.kc || 0.7;
    const deficit = (b.deficit_pct ?? 100) / 100;
    const area = 43560 / b.vines_per_acre;
    return Math.round(e.et0_in * kc * area * 0.623 * deficit * 10) / 10;
  };

  return (
    <div className="space-y-3" data-testid="irr-schedule-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="week-prev" onClick={() => shift(-1)} className="h-10 w-10 border border-stone-300 flex items-center justify-center bg-white"><ChevronLeft size={16} /></button>
        <div className="font-bold" data-testid="week-label" style={{ fontFamily: "'Fraunces', serif" }}>Week of {weekLabel(week)}</div>
        <button data-testid="week-next" onClick={() => shift(1)} className="h-10 w-10 border border-stone-300 flex items-center justify-center bg-white"><ChevronRight size={16} /></button>
        <div className="flex flex-wrap gap-1.5 ml-auto">
          {Object.entries(et).map(([rn, v]) => (
            <span key={rn} data-testid={`et-chip-${rn}`} className="text-[11px] font-semibold bg-sky-50 border border-sky-200 text-sky-800 px-2 py-1 flex items-center gap-1">
              <Droplets size={12} /> Ranch {rn} · ET₀ {v.et0_in}" this week
            </span>
          ))}
          {Object.keys(et).length === 0 && (
            <span className="text-[11px] text-stone-400">No ET data — set a weather station in Pest Intelligence → Weather</span>
          )}
        </div>
      </div>

      <div className="bg-white border border-stone-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead>
            <tr className="bg-[#1F3D34] text-white text-left text-[10px] uppercase">
              <th className="p-3">Block</th><th className="p-3">ET₀ (wk)</th><th className="p-3">Soil Moisture</th>
              <th className="p-3">Visual</th><th className="p-3 text-right">Gal / Vine</th><th className="p-3 text-right">Total Gal</th>
              <th className="p-3 text-right">Hours</th><th className="p-3 text-right">Sets / wk</th>
              <th className="p-3 text-right">Actual hrs</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {ready.map((b) => {
              const r = get(b.id); const c = calc(b); const s = savedFor(b.id); const sug = suggest(b);
              return (
                <tr key={b.id} data-testid={`sched-row-${b.id}`} className="border-t border-stone-200">
                  <td className="p-3 font-bold whitespace-nowrap">{b.block || b.name}
                    <div className="text-[10px] font-normal text-stone-500">{b.ranch_number} · {b.name} · {b.acres} ac · {vinesOf(b)?.toLocaleString()} vines</div></td>
                  <td className="p-3 text-sky-800 font-semibold">{et[b.ranch_number] ? `${et[b.ranch_number].et0_in}"` : "—"}</td>
                  <td className="p-2"><input data-testid={`soil-${b.id}`} value={r.soil_moisture} onChange={(e) => set(b.id, { soil_moisture: e.target.value })} className={inp} placeholder="e.g. 32 cb / dry" /></td>
                  <td className="p-2"><input data-testid={`visual-${b.id}`} value={r.visual_assessment} onChange={(e) => set(b.id, { visual_assessment: e.target.value })} className={inp} placeholder="e.g. mild stress" /></td>
                  <td className="p-2">
                    <input data-testid={`gpv-${b.id}`} type="number" step="0.5" min="0" value={r.gpv} onChange={(e) => set(b.id, { gpv: e.target.value })} className={`${inp} text-right font-bold w-24`} placeholder="0" />
                    {sug != null && (
                      <button type="button" data-testid={`suggest-${b.id}`} onClick={() => set(b.id, { gpv: String(sug) })}
                        title={`ET₀ ${et[b.ranch_number].et0_in}" × Kc ${b.kc || 0.7} × ${(43560 / b.vines_per_acre).toFixed(0)} sqft/vine × 0.623 × ${(b.deficit_pct ?? 100)}% deficit`}
                        className="mt-1 text-[10px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 whitespace-nowrap hover:bg-sky-100 block">
                        ≈ {sug} suggested
                      </button>
                    )}
                  </td>
                  <td className="p-3 text-right text-stone-500" data-testid={`totalgal-${b.id}`}>{c.gallons != null ? c.gallons.toLocaleString() : "—"}</td>
                  <td className="p-3 text-right font-bold" data-testid={`hours-${b.id}`}>{c.hours ?? "—"}</td>
                  <td className="p-3 text-right font-bold" data-testid={`sets-${b.id}`}>{c.sets != null ? `${c.sets} × ${b.hours_per_set}h` : c.hours != null ? "set hrs/set" : "—"}</td>
                  <td className={`p-3 text-right font-bold ${s && s.actual_hours >= s.hours ? "text-[#1F3D34]" : "text-stone-500"}`}>{s ? s.actual_hours : "—"}</td>
                  <td className="p-2 whitespace-nowrap">
                    <button data-testid={`save-sched-${b.id}`} onClick={() => save(b)} className="h-9 px-3 bg-[#1F3D34] text-white text-xs font-bold uppercase inline-flex items-center gap-1"><Save size={12} /> Save</button>
                    {s && <button data-testid={`del-sched-${b.id}`} onClick={() => remove(b)} className="h-9 w-9 border border-stone-300 text-red-700 inline-flex items-center justify-center ml-1"><Trash2 size={12} /></button>}
                  </td>
                </tr>
              );
            })}
            {ready.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-stone-400 font-bold uppercase text-sm">No blocks are set up yet — add emitter info in Block Setup</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {notReady.length > 0 && (
        <div className="text-xs text-amber-700 font-semibold" data-testid="sched-missing-setup">
          {notReady.length} block{notReady.length === 1 ? "" : "s"} missing emitter setup (rate, emitters/vine, vines/acre) —{" "}
          <button onClick={goSetup} className="underline">open Block Setup</button>
        </div>
      )}
    </div>
  );
}
